import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { memoApi } from '../services/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  width: 100%;
  max-width: 900px;
  max-height: 85vh;
  overflow-y: auto;

  h2 {
    margin-top: 0;
    color: #333;
  }
  position: relative;
`;

/*
 * 메모 창에는 닫는 버튼이 없고 바깥을 눌러 닫게 되어 있었다. 바깥 누르기를
 * 없애면 나갈 길이 사라지므로 여기서 하나 만든다.
 */
const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  border-radius: 9px;
  font-size: 20px;
  line-height: 1;
  color: #bbb;
  cursor: pointer;

  &:hover { background: #f2f2f2; color: #666; }
`;

const EmptyHint = styled.p`
  color: #999;
  text-align: center;
  padding: 30px 0;
`;

const MemoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
`;

const AddMemoCard = styled.button`
  min-height: 110px;
  border-radius: 8px;
  border: 2px dashed #ccc;
  background: transparent;
  color: #999;
  font-size: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s ease-in-out, color 0.15s ease-in-out;

  &:hover {
    border-color: #007bff;
    color: #007bff;
  }
`;

const MemoCard = styled.div`
  background-color: ${props => props.color || '#fff59d'};
  border-radius: 8px;
  padding: 10px;
  min-height: 110px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  position: relative;
  cursor: pointer;
  transition: transform 0.15s ease-in-out;

  &:hover {
    transform: translateY(-2px);
  }
`;

const MemoDeleteButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.4);
  line-height: 0;
  padding: 5px;
  border-radius: 4px;
  z-index: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
    color: rgba(0, 0, 0, 0.7);
  }
`;

const MemoPreview = styled.div`
  flex: 1;
  margin-top: 16px;
  font-size: 13px;
  line-height: 1.4;
  color: #3a3a3a;
  white-space: pre-wrap;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
`;

const MemoPlaceholder = styled(MemoPreview)`
  color: rgba(0, 0, 0, 0.35);
`;

const EditorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  /* 창 닫기 ×(오른쪽 위에 떠 있음) 자리를 비워 둔다.
     비워 두지 않으면 삭제 버튼과 ×가 겹쳐서 어느 쪽인지 알 수 없다. */
  padding-right: 40px;
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #007bff;
  padding: 6px 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const EditorDeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
  padding: 6px 8px;
  border-radius: 6px;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
    color: #c62828;
  }
`;

/*
 * 삭제는 휴지통으로 그린다.
 *
 * ×로 두면 창 닫기 ×와 같은 모양이라, 메모를 지우려다 창을 닫거나 창을
 * 닫으려다 메모를 지우게 된다. 둘 중 하나는 되돌릴 수 없는 쪽이다.
 */
function TrashIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 6h18" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

const MemoEditorCard = styled.div`
  background-color: ${props => props.color || '#fff59d'};
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
`;

const MemoTextarea = styled.textarea`
  width: 100%;
  min-height: 420px;
  border: none;
  background: transparent;
  resize: vertical;
  font-family: inherit;
  font-size: 20px;
  line-height: 1.6;
  color: #3a3a3a;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: rgba(0, 0, 0, 0.35);
  }
`;

const SAVE_DEBOUNCE_MS = 600;

function MemoModal({ isOpen, onClose }) {
  const [memos, setMemos] = useState([]);
  const [editingMemoId, setEditingMemoId] = useState(null);
  const saveTimers = useRef({});
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchMemos();
    } else {
      setEditingMemoId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (editingMemoId && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [editingMemoId]);

  useEffect(() => {
    // Flush any pending debounced saves when the modal closes/unmounts
    return () => {
      Object.values(saveTimers.current).forEach(clearTimeout);
      saveTimers.current = {};
    };
  }, []);

  const fetchMemos = async () => {
    try {
      const response = await memoApi.getAll();
      setMemos(response.data || []);
    } catch (error) {
      console.error('Error fetching memos:', error);
    }
  };

  const handleAddMemo = async () => {
    try {
      const response = await memoApi.create('');
      setMemos((prev) => [response.data, ...prev]);
      setEditingMemoId(response.data.id);
    } catch (error) {
      console.error('Error creating memo:', error);
      alert('메모 생성에 실패했습니다.');
    }
  };

  const scheduleSave = (id, content) => {
    if (saveTimers.current[id]) {
      clearTimeout(saveTimers.current[id]);
    }
    saveTimers.current[id] = setTimeout(() => {
      delete saveTimers.current[id];
      memoApi.update(id, content).catch((error) => {
        console.error('Error saving memo:', error);
      });
    }, SAVE_DEBOUNCE_MS);
  };

  const handleChangeContent = (id, content) => {
    setMemos((prev) => prev.map((memo) => (memo.id === id ? { ...memo, content } : memo)));
    scheduleSave(id, content);
  };

  const handleBlur = (id, content) => {
    if (saveTimers.current[id]) {
      clearTimeout(saveTimers.current[id]);
      delete saveTimers.current[id];
    }
    memoApi.update(id, content).catch((error) => {
      console.error('Error saving memo:', error);
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 메모를 삭제하시겠습니까?')) return;
    try {
      await memoApi.delete(id);
      setMemos((prev) => prev.filter((memo) => memo.id !== id));
      if (editingMemoId === id) setEditingMemoId(null);
    } catch (error) {
      console.error('Error deleting memo:', error);
      alert('메모 삭제에 실패했습니다.');
    }
  };

  if (!isOpen) return null;

  const editingMemo = memos.find((memo) => memo.id === editingMemoId);

  // 바깥을 눌러도 닫지 않는다. 적던 내용이 통째로 날아가는 것보다
  // 닫는 버튼을 한 번 더 누르는 편이 낫다.
  return (
    <ModalOverlay>
      <ModalContent>
        <CloseButton onClick={onClose} title="닫기" aria-label="닫기">×</CloseButton>
        {editingMemo ? (
          <>
            <EditorHeader>
              <BackButton onClick={() => setEditingMemoId(null)}>← 목록으로</BackButton>
              <EditorDeleteButton
                onClick={() => handleDelete(editingMemo.id)}
                title="이 메모 삭제"
                aria-label="이 메모 삭제"
              >
                <TrashIcon size={16} />
                삭제
              </EditorDeleteButton>
            </EditorHeader>
            <MemoEditorCard color={editingMemo.color}>
              <MemoTextarea
                ref={textareaRef}
                value={editingMemo.content}
                placeholder="메모를 입력하세요"
                onChange={(e) => handleChangeContent(editingMemo.id, e.target.value)}
                onBlur={(e) => handleBlur(editingMemo.id, e.target.value)}
              />
            </MemoEditorCard>
          </>
        ) : (
          <>
            <h2>📝 메모</h2>
            <MemoGrid>
              <AddMemoCard onClick={handleAddMemo} title="새 메모 추가">+</AddMemoCard>
              {memos.map((memo) => (
                <MemoCard key={memo.id} color={memo.color} onClick={() => setEditingMemoId(memo.id)}>
                  <MemoDeleteButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(memo.id);
                    }}
                    title="이 메모 삭제"
                    aria-label="이 메모 삭제"
                  >
                    <TrashIcon size={14} />
                  </MemoDeleteButton>
                  {memo.content ? (
                    <MemoPreview>{memo.content}</MemoPreview>
                  ) : (
                    <MemoPlaceholder>메모를 입력하세요</MemoPlaceholder>
                  )}
                </MemoCard>
              ))}
            </MemoGrid>
            {memos.length === 0 && (
              <EmptyHint>아직 메모가 없습니다. + 를 눌러 새 메모를 추가하세요.</EmptyHint>
            )}
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
}

export default MemoModal;
